import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API_BASE } from '../config/api';
import client from '../mqttService';
import Select, { components as RSComponents } from 'react-select';
import { GiSunrise, GiSunset } from 'react-icons/gi';
import ScheduleDial from './ScheduleDial';

/* dnd-kit */
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/* ----------------------- Données Select ----------------------- */
const SUN_EVENTS = [
  { type: 'sun', value: 'sunrise', label: 'Lever',  labelText: 'Lever',
    icon: <GiSunrise style={{ fontSize: 20, color: '#fa4d1d' }} /> },
  { type: 'sun', value: 'sunset',  label: 'Coucher', labelText: 'Coucher',
    icon: <GiSunset  style={{ fontSize: 20, color: '#e0123b' }} /> }
];

const SUN_OFFSETS = [
  { label: '-1h', value: -60 },
  { label: '-30m', value: -30 },
  { label: '0', value: 0 },
  { label: '+30m', value: 30 },
  { label: '+1h', value: 60 }
];

const FIXED_HOURS = Array.from({ length: 24 }, (_, i) => ({
  type: 'fixed',
  label: `${i < 10 ? '0' : ''}${i}h`,
  value: `${i < 10 ? '0' : ''}${i}`
}));

const FIXED_MINUTES = [0,5,10,15,20,25,30,35,40,45,50,55].map(m => ({
  label: `${m < 10 ? '0' : ''}${m}m`,
  value: `${m < 10 ? '0' : ''}${m}`
}));

/* Placeholders */
const HOURS_PLACEHOLDER_OPT   = [{ type:'placeholder', value:'__hours__',   label:'Heures',  isDisabled:true }];
const MINUTES_PLACEHOLDER_OPT = [{                         value:'__minutes__', label:'Minutes', isDisabled:true }];
const HOURS_PLACEHOLDER_TEXT   = 'Heures';
const MINUTES_PLACEHOLDER_TEXT = 'Minutes';

/* -------- Affichage lecture (icône soleil + offset) -------- */
function renderSunIconOnly(evType, evValue, offsetOrMin, { bigger = false } = {}) {
  if (evType !== 'sun') {
    return <span style={{ whiteSpace: 'nowrap' }}>{evValue}:{offsetOrMin}</span>;
  }
  const size = bigger ? 22 : 20;
  const v = Number(offsetOrMin);
  let offset = '';
  if (!Number.isNaN(v) && v !== 0) {
    offset = v < 0
      ? (Math.abs(v) % 60 === 0 ? ` -${Math.abs(v) / 60}h` : ` -${Math.abs(v)}m`)
      : (v % 60 === 0 ? ` +${v / 60}h` : ` +${v}m`);
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
      {evValue === 'sunrise'
        ? <GiSunrise style={{ fontSize: size, color: '#fa4d1d' }} />
        : <GiSunset  style={{ fontSize: size, color: '#e0123b' }} />}
      {!!offset && <span style={{ fontSize: '0.95em' }}>{offset}</span>}
    </span>
  );
}

/* ----------------------- Select helpers ----------------------- */
const selectMenuPortalTarget = typeof window !== 'undefined' ? document.body : undefined;
const selectMenuPortalStyle = { zIndex: 2600, maxWidth: '98vw', minWidth: 80 };
const SELECT_NO_ARROW = { IndicatorSeparator:null, DropdownIndicator:null, ClearIndicator:null };

const IconOnlyOption = (props) => {
  const { data } = props;
  if (data.type === 'sun') {
    const icon = SUN_EVENTS.find(e => e.value === data.value)?.icon;
    return (
      <RSComponents.Option {...props}>
        <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}>{icon}</span>
      </RSComponents.Option>
    );
  }
  return <RSComponents.Option {...props}>{props.children}</RSComponents.Option>;
};

const IconOnlySingleValue = (props) => {
  const { data } = props;
  if (data.type === 'sun') {
    const icon = SUN_EVENTS.find(e => e.value === data.value)?.icon;
    return (
      <RSComponents.SingleValue {...props}>
        <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}>{icon}</span>
      </RSComponents.SingleValue>
    );
  }
  return <RSComponents.SingleValue {...props}>{props.children}</RSComponents.SingleValue>;
};

const SELECT_SUN_ICON = { ...SELECT_NO_ARROW, Option: IconOnlyOption, SingleValue: IconOnlySingleValue };

/* styles: possibilité de réduire placeholder / value via opts */
function makeSelectStyles(width, opts = {}) {
  const { placeholderFS, valueFS } = opts;
  return {
    control: (base) => ({ ...base, fontSize: valueFS ?? 15, width, minWidth:width, maxWidth:width, borderRadius:4 }),
    menuPortal: (base) => ({ ...base, ...selectMenuPortalStyle }),
    menu: (base) => ({ ...base, width, minWidth:70, maxWidth:'99vw', zIndex:3000 }),
    option: (provided, state) => ({
      ...provided,
      whiteSpace:'nowrap',
      backgroundColor: state.isSelected ? '#e7f1ff' : (state.isFocused ? '#f0f6ff' : 'white'),
      color:'#111'
    }),
    singleValue: (base) => ({ ...base, whiteSpace:'nowrap' }),
    placeholder: (base) => ({
      ...base,
      fontSize: placeholderFS ?? (valueFS ?? 15),
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    })
  };
}

/* Utilitaire non-réductible */
const noShrink = { flex: '0 0 auto' };

/* ----------------------- Sortable row ----------------------- */
function SortableScheduleRow({
  s,
  responsive,
  scheduleDispStyle,
  formatDaysDisplay,
  renderTime,
  startEditing,
  deleteSchedule
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: String(s.id) });

  const style = {
    ...scheduleDispStyle,
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.92 : 1,
    background: isDragging ? '#f6f9ff' : scheduleDispStyle.backgroundColor,
    borderColor: isDragging ? '#9cc5ff' : '#bbb',
    cursor: 'pointer'
  };

  const onType  = (s.on_type  && s.on_type  !== 'fixed') ? 'sun' : 'fixed';
  const offType = (s.off_type && s.off_type !== 'fixed') ? 'sun' : 'fixed';
  const onH  = onType  === 'sun' ? s.on_type  : (s.on_time  || '').split(':')[0];
  const onM  = onType  === 'sun' ? s.on_offset : (s.on_time  || '').split(':')[1];
  const offH = offType === 'sun' ? s.off_type : (s.off_time || '').split(':')[0];
  const offM = offType === 'sun' ? s.off_offset: (s.off_time || '').split(':')[1];

  const stopAll = (e) => { e.stopPropagation(); };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      title="Cliquer pour modifier • Glisser avec la poignée pour réordonner"
      onClick={() => startEditing(s)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') startEditing(s); }}
      role="button"
      tabIndex={0}
    >
      {/* Poignée = drag uniquement */}
      <div
        {...listeners}
        style={{
          padding: responsive ? '0 4px' : '0 8px',
          userSelect: 'none',
          marginRight: responsive ? 4 : 8,
          fontSize: responsive ? 14 : 18,
          lineHeight: 1,
          cursor: 'grab'
        }}
        onClick={stopAll}
        title="Déplacer"
        aria-label="Déplacer"
      >
        ⋮⋮
      </div>

      <div style={{ minWidth: responsive ? 35 : 100, fontWeight: 700, fontSize: responsive ? 11 : 15.2, paddingRight: responsive ? 6 : 10, whiteSpace: 'nowrap' }}>
        {formatDaysDisplay(s.days)}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', flex: 1, gap: responsive ? 6 : 12, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: responsive ? 4 : 8 }}>
          de {renderTime(onType, onH, onM)}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: responsive ? 4 : 8 }}>
          à {renderTime(offType, offH, offM)}
        </span>
      </div>

      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }} onClick={stopAll}>
        <button
          style={{
            border: 'none', background: 'none',
            fontSize: responsive ? 12 : 20, cursor: 'pointer', margin: 0,
            padding: responsive ? '0 3px' : '0 6px',
            minWidth: responsive ? 16 : 28,
            marginRight: responsive ? 7 : 10
          }}
          title="Modifier"
          onClick={(e) => { e.stopPropagation(); startEditing(s); }}
        >✏️</button>

        {/* Corbeille +20% */}
        <button
          style={{
            border: 'none', background: 'none',
            fontSize: responsive ? 14 : 24,
            cursor: 'pointer', margin: 0,
            padding: responsive ? '0 3px' : '0 6px',
            minWidth: responsive ? 16 : 28
          }}
          title="Supprimer"
          onClick={(e) => { e.stopPropagation(); deleteSchedule(s.id); }}
        >🗑️</button>
      </div>
    </div>
  );
}

/* ----------------------- Composant principal ----------------------- */
function DeviceCard({ device, onClose, relayState: relayStateProp }) {
  const [schedules, setSchedules] = useState([]);

  /* Nouveaux plannings → null pour afficher Heures/Minutes */
  const [onHourType, setOnHourType] = useState(null);
  const [onHourValue, setOnHourValue] = useState(null);
  const [onMinuteOrOffset, setOnMinuteOrOffset] = useState(null);
  const [offHourType, setOffHourType] = useState(null);
  const [offHourValue, setOffHourValue] = useState(null);
  const [offMinuteOrOffset, setOffMinuteOrOffset] = useState(null);

  /* Édition existants */
  const [editingScheduleId, setEditingScheduleId] = useState(null);
  const [editingOnType, setEditingOnType] = useState('sun');
  const [editingOnValue, setEditingOnValue] = useState('sunrise');
  const [editingOnMinuteOrOffset, setEditingOnMinuteOrOffset] = useState(0);
  const [editingOffType, setEditingOffType] = useState('sun');
  const [editingOffValue, setEditingOffValue] = useState('sunset');
  const [editingOffMinuteOrOffset, setEditingOffMinuteOrOffset] = useState(0);

  const [selectedDaysForNew, setSelectedDaysForNew] = useState([]);
  const [editingDays, setEditingDays] = useState([]);
  const [refresh, setRefresh] = useState(false);

  /* Popup "cadran horaire" (nouveau planning / édition) */
  const [dialOpenNew, setDialOpenNew] = useState(false);
  const [dialOpenEdit, setDialOpenEdit] = useState(false);
  const days = useMemo(() => ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'], []);

  const [relayState, setRelayState] = useState(!!relayStateProp);
  const [pending, setPending] = useState(false);
  const [sunTimes, setSunTimes] = useState(null);

  /* Responsive */
  const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const responsive = windowWidth < 600;

  const HOUR_W = responsive ? 62 : 100;
  const MIN_W  = responsive ? 56 : 80;
  const GAP_W  = responsive ? 3 : 6;

  /* Largeurs reduites pour la ligne d'edition (existant), a la fois en
     mobile et en desktop/paysage : la ligne doit aussi tenir dans une
     fenetre PC etroite ou un iPhone en paysage (~600-900px), pas
     seulement sous 600px - d'ou un budget resserre dans les deux cas au
     lieu d'un seul palier "mobile". Minutes un peu plus large que Heures
     car sans fleche de menu, le texte y est plus facilement tronque. */
  const HOUR_W_EDIT = responsive ? 48 : 88;
  const MIN_W_EDIT  = responsive ? 54 : 74;

  /* Espacements */
  const HM_GAP = responsive ? 2 : 6;            // Heures ↔ Minutes
  const DF_GAP_EDIT = responsive ? 3 : 10;      // Début ↔ Fin (existants) — réduit
  const DF_GAP_NEW  = responsive ? 10 : 12;     // Début ↔ Fin (nouveau)

  /* ----- Tri par heure/minute ----- */
function sortSchedulesByTime(list) {
  return [...list].sort((a, b) => {
    const ha = Number(a.start_hour) || 0;
    const hb = Number(b.start_hour) || 0;
    if (ha !== hb) return ha - hb;

    const ma = Number(a.start_min) || 0;
    const mb = Number(b.start_min) || 0;
    return ma - mb;
  });
}

/* ----- Gestion ordre local + serveur (drag & drop) ----- */
const lsKey = `scheduleOrder:${device?.id}`;
const toKey = (x) => String(x);
function saveOrderLocally(ids) {
    try { localStorage.setItem(lsKey, ids.map(toKey).join(',')); } catch {}
  }

  async function persistOrderServer(ids) {
    const idsStr = ids.map(toKey);
    try {
      const r = await fetch(`${API_BASE}/schedule/reorder_device_schedules.php`, {
        method:'POST', headers:{'Content-Type':'application/json'}, credentials:'include',
        body: JSON.stringify({ device_id: device.id, order: idsStr })
      });
      if (r.ok) return true;
    } catch {}
    try {
      const r2 = await fetch(`${API_BASE}/schedule/update_order.php`, {
        method:'POST', headers:{'Content-Type':'application/json'}, credentials:'include',
        body: JSON.stringify({ device_id: device.id, order_csv: idsStr.join(',') })
      });
      if (r2.ok) return true;
    } catch {}
    return false;
  }

  /* Sync Relay + MQTT */
  useEffect(() => {
    if (pending && typeof relayStateProp !== 'undefined' && relayStateProp === relayState) setPending(false);
  }, [relayStateProp, relayState, pending]);

  useEffect(() => {
    const mac = (device.identifier || device.mac || '').replace(/[:-]/g, '').toUpperCase();
    if (!mac) return;
    function handleMqttMessage(topic, message) {
      if (topic === `sonoff/state/${mac}`) { setRelayState(message.toString() === 'ON'); setPending(false); }
    }
    client.subscribe(`sonoff/state/${mac}`);
    client.on('message', handleMqttMessage);
    return () => { client.unsubscribe(`sonoff/state/${mac}`); client.off('message', handleMqttMessage); };
  }, [device.identifier, device.mac]);

  useEffect(() => { setRelayState(Number(relayStateProp) === 1); setPending(false); }, [relayStateProp, device.id]);

  /* Fetch schedules */
  const fetchSchedules = useCallback(() => {
  fetch(`${API_BASE}/schedule/get_schedules.php?device_id=${device.id}`, { credentials:'include' })
    .then(res => (res.ok ? res.json() : Promise.reject()))
    .then(data => setSchedules(sortSchedulesByTime(data.schedules || [])))
    .catch(() => setSchedules([]));
}, [device.id]);

  useEffect(() => { fetchSchedules(); }, [fetchSchedules, refresh]);

  /* Fetch lever/coucher du soleil pour aujourd'hui */
  useEffect(() => {
    fetch(`${API_BASE}/schedule/get_sun_times.php?device_id=${device.id}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => { if (d.sunrise && d.sunset) setSunTimes(d); })
      .catch(() => {});
  }, [device.id]);

  /* Edition helpers */
  function initEditFields(s) {
    if (s.on_type && s.on_type !== 'fixed') {
      setEditingOnType('sun'); setEditingOnValue(s.on_type); setEditingOnMinuteOrOffset(Number(s.on_offset) || 0);
    } else {
      setEditingOnType('fixed'); const [hh,mm]=(s.on_time||'00:00').split(':'); setEditingOnValue(hh||'00'); setEditingOnMinuteOrOffset(mm||'00');
    }
    if (s.off_type && s.off_type !== 'fixed') {
      setEditingOffType('sun'); setEditingOffValue(s.off_type); setEditingOffMinuteOrOffset(Number(s.off_offset)||0);
    } else {
      setEditingOffType('fixed'); const [hh,mm]=(s.off_time||'00:00').split(':'); setEditingOffValue(hh||'00'); setEditingOffMinuteOrOffset(mm||'00');
    }
    if (Array.isArray(s.days)) setEditingDays(s.days.map(Number));
    else if (typeof s.days === 'string') setEditingDays(s.days.split(',').map(Number));
    else setEditingDays([]);
  }

  const addSchedule = () => {
    if (!onHourValue || onMinuteOrOffset == null || !offHourValue || offMinuteOrOffset == null || !selectedDaysForNew.length) {
      alert('Veuillez remplir les horaires et choisir les jours'); return;
    }
    const data = { device_id: device.id, days: selectedDaysForNew.join(',') };
    if (onHourType === 'sun') { data.on_type = onHourValue; data.on_offset = parseInt(onMinuteOrOffset,10); }
    else { data.on_type = 'fixed'; data.on_time = onHourValue + ':' + onMinuteOrOffset; data.on_offset = 0; }
    if (offHourType === 'sun') { data.off_type = offHourValue; data.off_offset = parseInt(offMinuteOrOffset,10); }
    else { data.off_type = 'fixed'; data.off_time = offHourValue + ':' + offMinuteOrOffset; data.off_offset = 0; }

    fetch(`${API_BASE}/schedule/add_device_schedule.php`, {
      method:'POST', headers:{'Content-Type':'application/json'}, credentials:'include', body: JSON.stringify(data)
    })
      .then(res => { if (!res.ok) throw new Error(); setDialOpenNew(false); resetNewInputs(); setRefresh(r=>!r); })
      .catch(() => alert('Erreur lors de l’ajout de la programmation.'));
  };

  const resetNewInputs = () => {
    setOnHourType(null); setOnHourValue(null); setOnMinuteOrOffset(null);
    setOffHourType(null); setOffHourValue(null); setOffMinuteOrOffset(null);
    setSelectedDaysForNew([]);
  };

  /* Ouvre la popup cadran pour un nouveau planning : initialise des valeurs
     par défaut si rien n'a encore été choisi, pour que le cadran affiche
     tout de suite une position valide. */
  const openNewDial = () => {
    if (onHourType == null) { setOnHourType('fixed'); setOnHourValue('19'); setOnMinuteOrOffset('00'); }
    if (offHourType == null) { setOffHourType('fixed'); setOffHourValue('23'); setOffMinuteOrOffset('00'); }
    setDialOpenNew(true);
  };

  const deleteSchedule = (id) => {
    fetch(`${API_BASE}/schedule/delete_device_schedule.php`, {
      method:'POST', headers:{'Content-Type':'application/json'}, credentials:'include',
      body: JSON.stringify({ schedule_id:id })
    }).then(() => {
      setSchedules(prev => {
        const next = prev.filter(x => String(x.id) !== String(id));
        saveOrderLocally(next.map(x => x.id)); return next;
      });
    });
  };

  const startEditing = (s) => { setEditingScheduleId(s.id); initEditFields(s); };
  const cancelEditing = () => {
    setEditingScheduleId(null);
    setEditingOnType('sun'); setEditingOnValue('sunrise'); setEditingOnMinuteOrOffset(0);
    setEditingOffType('sun'); setEditingOffValue('sunset'); setEditingOffMinuteOrOffset(0);
    setEditingDays([]);
    setDialOpenEdit(false);
  };
  const saveScheduleEdit = () => {
    const data = { schedule_id: editingScheduleId, days: editingDays.join(',') };
    if (editingOnType === 'sun') { data.on_type = editingOnValue; data.on_offset = parseInt(editingOnMinuteOrOffset,10); }
    else { data.on_type = 'fixed'; data.on_time = editingOnValue + ':' + editingOnMinuteOrOffset; data.on_offset = 0; }
    if (editingOffType === 'sun') { data.off_type = editingOffValue; data.off_offset = parseInt(editingOffMinuteOrOffset,10); }
    else { data.off_type = 'fixed'; data.off_time = editingOffValue + ':' + editingOffMinuteOrOffset; data.off_offset = 0; }

    fetch(`${API_BASE}/schedule/update_schedule.php`, {
      method:'POST', headers:{'Content-Type':'application/json'}, credentials:'include', body: JSON.stringify(data)
    }).then(() => { setDialOpenEdit(false); cancelEditing(); setRefresh(r=>!r); });
  };

  const toggleDaySelection = (i) =>
    setSelectedDaysForNew(prev => (prev.includes(i) ? prev.filter(d => d !== i) : [...prev, i]));

  const formatDaysDisplay = useCallback((daysValue) => {
    let indices = [];
    if (Array.isArray(daysValue)) indices = daysValue;
    else if (typeof daysValue === 'string') indices = daysValue.split(',').map(d => parseInt(d.trim(),10));
    indices = Array.from(new Set(indices)).filter(i => i>=0 && i<=6).sort((a,b)=>a-b);
    if (indices.length === 7) return 'Tous les jours';
    if (!indices.length) return '';
    return indices.map(i => days[i]).join(', ');
  }, [days]);

  let relayText = ''; let relayColor = '';
  if (relayState === true) { relayText = 'Allumé'; relayColor = '#11a611'; }
  else if (relayState === false) { relayText = 'Éteint'; relayColor = '#db1b1b'; }

  const scheduleDispStyle = {
    display:'flex', alignItems:'center',
    padding: responsive ? '7px 3px' : '8px 26px',
    backgroundColor:'#fff', margin: responsive ? '5px 0' : '9px 0',
    borderRadius:9, border:'1.5px solid #bbb', boxShadow:'0 2px 7px rgba(0,0,0,0.04)',
    gap: responsive ? 4 : 14, minHeight: responsive ? 24 : 44,
    fontSize: responsive ? 12.7 : 15.2, flexWrap:'nowrap', width:'100%', overflowX:'auto'
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!over || String(active.id) === String(over.id)) return;
    setSchedules(prev => {
      const oldIndex = prev.findIndex(x => String(x.id) === String(active.id));
      const newIndex = prev.findIndex(x => String(x.id) === String(over.id));
      if (oldIndex === -1 || newIndex === -1) return prev;
      const next = arrayMove(prev, oldIndex, newIndex);
      const ids = next.map(x => x.id); saveOrderLocally(ids); persistOrderServer(ids);
      return next;
    });
  };

  const newDirty =
    selectedDaysForNew.length > 0 ||
    onHourType != null || onHourValue != null || onMinuteOrOffset != null ||
    offHourType != null || offHourValue != null || offMinuteOrOffset != null;

  return (
    <div style={{
      width:'100vw', minHeight:'100vh', background:'rgba(0,0,0,0.28)',
      display:'flex', alignItems:'flex-start', justifyContent:'center',
      padding: responsive ? '10px 0' : '30px 0',
      position:'fixed', top:0, left:0, zIndex:2220,
      overflowY:'auto', maxHeight:'100dvh', height:'100dvh', WebkitOverflowScrolling:'touch',
      boxSizing:'border-box'
    }}>
      <div className="devicecard-box-inner" style={{
        background:'#fff', padding: responsive ? 7 : 22, borderRadius: responsive ? 7 : 14,
        boxShadow:'0 7px 30px rgba(0,0,0,0.22)', maxWidth: responsive ? '100vw' : 640,
        minWidth: responsive ? 'unset' : 420, width:'100%', position:'relative',
        margin:'0 auto', overflowX:'auto'
      }}>
        {/* En-tête */}
        <div style={{ display:'flex', alignItems:'center', position:'relative', minHeight:36, flexWrap:'nowrap' }}>
          <div style={{ flex:2, minWidth:55, maxWidth: responsive ? '62vw' : '55%', overflow:'hidden' }}>
            <h3 style={{ margin:0, fontSize: responsive ? 13 : 21.6, lineHeight:'1.15', textAlign:'left', overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis' }}>
              {device.name || device.device_name}
              {relayText && <span style={{ marginLeft:12, color:relayColor, fontWeight:700, fontSize: responsive ? 11.5 : 15.5, letterSpacing:0.1, verticalAlign:'middle', whiteSpace:'nowrap' }}>({relayText})</span>}
            </h3>
            {sunTimes && (() => {
              // Heure locale du device dans son fuseau horaire
              let localTime = null;
              if (sunTimes.timezone) {
                try {
                  const s = new Date().toLocaleTimeString('fr-FR', {
                    timeZone: sunTimes.timezone,
                    hour: '2-digit', minute: '2-digit', hour12: false
                  });
                  localTime = s.replace(':', 'h');
                } catch {}
              }
              return (
                <div style={{ fontSize: responsive ? 10 : 12, color: '#555', marginTop: 3 }}>
                  {/* Ligne 1 : ville + heure locale */}
                  <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {device.city_name && <span style={{ fontStyle: 'italic' }}>{device.city_name}</span>}
                    {localTime && <span style={{ color: '#777' }}> — {localTime}</span>}
                  </div>
                  {/* Ligne 2 : lever / coucher avec icônes planning */}
                  <div style={{ display: 'flex', gap: responsive ? 8 : 16, marginTop: 1, whiteSpace: 'nowrap' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      <GiSunrise style={{ fontSize: responsive ? 12 : 14, color: '#fa4d1d', verticalAlign: 'middle' }} />
                      <span>Lever : <strong>{sunTimes.sunrise}</strong></span>
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      <GiSunset style={{ fontSize: responsive ? 12 : 14, color: '#e0123b', verticalAlign: 'middle' }} />
                      <span>Coucher : <strong>{sunTimes.sunset}</strong></span>
                    </span>
                  </div>
                </div>
              );
            })()}
          </div>
          <button onClick={onClose} style={{
            background:'#f2f2fa', fontSize: responsive ? 18 : 34, width: responsive ? 26 : 52, height: responsive ? 26 : 52,
            borderRadius:'50%', border:'1.1px solid #c23', color:'#c23', boxShadow:'0 1.5px 8px rgba(180,10,35,0.09)',
            position:'absolute', top:0, right:0, zIndex:9, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer'
          }} title="Fermer cette fiche">✖️</button>
        </div>

        {/* ------- PLANNINGS EXISTANTS -------- */}
        <div style={{ marginTop:8, maxWidth:'100vw', marginLeft:'auto', marginRight:'auto' }}>
          <strong style={{ fontSize: responsive ? 12.7 : 16.5, textDecoration:'underline' }}>Plannings existants :</strong>

          <div style={{ marginTop:8, maxHeight: responsive ? '39vh' : '300px', overflowY:'auto', overflowX:'hidden', paddingRight:2, WebkitOverflowScrolling:'touch' }}>
            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={schedules.filter(s => s.id !== editingScheduleId).map(s => String(s.id))} strategy={verticalListSortingStrategy}>
                {schedules.map((s) =>
                  editingScheduleId === s.id ? (
                    <div key={s.id} style={{
                      display:'flex', flexDirection:'column',
                      padding: responsive ? '7px 6px' : '11px 16px',
                      backgroundColor:'#e9f7f2',
                      margin: responsive ? '5px 0' : '8px 0', borderRadius:8, border:'3px solid #2272cf',
                      boxShadow:'0 2px 7px rgba(0,0,0,0.07)', gap: responsive ? 6 : 10, width:'100%', overflowX:'hidden'
                    }}>
                      {/* Jours */}
                      <div style={{ display:'flex', flexWrap:'nowrap', gap: responsive ? 2 : 6, marginBottom:6, alignItems:'center' }}>
                        {days.map((d, idx) => {
                          const selected = editingDays.includes(idx);
                          const BTN_W = responsive ? 33 : 60;
                          const BTN_H = responsive ? 24 : 29;
                          return (
                            <button
                              type="button" key={idx}
                              className={`day-toggle-btn${selected ? ' day-toggle-selected' : ''}`}
                              onClick={() => setEditingDays(prev => (prev.includes(idx) ? prev.filter(x => x !== idx) : [...prev, idx]))}
                              style={{
                                width:BTN_W, height:BTN_H, padding: responsive ? '1px 1px' : '2px 2px', fontSize: responsive ? 10.5 : 13.5,
                                borderRadius:4,
                                cursor:'pointer', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', fontWeight:500,
                                textAlign:'center'
                              }}
                              title={d}
                            >{d}</button>
                          );
                        })}
                      </div>

                      {/* Horaires édition + actions */}
                      <EditTimesRow
                        responsive={responsive}
                        HOUR_W={HOUR_W_EDIT}
                        MIN_W={MIN_W_EDIT}
                        GAP_W={GAP_W}
                        HM_GAP={HM_GAP}
                        DF_GAP={DF_GAP_EDIT}
                        editingOnType={editingOnType}
                        editingOnValue={editingOnValue}
                        editingOnMinuteOrOffset={editingOnMinuteOrOffset}
                        setEditingOnType={setEditingOnType}
                        setEditingOnValue={setEditingOnValue}
                        setEditingOnMinuteOrOffset={setEditingOnMinuteOrOffset}
                        editingOffType={editingOffType}
                        editingOffValue={editingOffValue}
                        editingOffMinuteOrOffset={editingOffMinuteOrOffset}
                        setEditingOffType={setEditingOffType}
                        setEditingOffValue={setEditingOffValue}
                        setEditingOffMinuteOrOffset={setEditingOffMinuteOrOffset}
                        rightActions={
                          <div style={{ ...noShrink, display:'flex', gap: responsive ? 2 : 6, alignItems:'center', marginLeft: responsive ? 3 : 8, marginRight: 0 }}>
                            <button
                              onClick={() => setDialOpenEdit(true)} title="Régler avec le cadran horaire"
                              style={{
                                width: responsive ? 24 : 30,
                                height: responsive ? 24 : 30,
                                display:'flex', alignItems:'center', justifyContent:'center',
                                borderRadius:6, border:'1px solid #3f4140', background:'#f9f9f9', color:'#262927',
                                boxShadow:'0 2px 8px rgba(0,0,0,0.08)', cursor:'pointer', fontSize: responsive ? 12 : 15
                              }}
                            >🕐</button>
                            <button
                              onClick={saveScheduleEdit} title="Enregistrer"
                              style={{
                                width: responsive ? 24 : 30,
                                height: responsive ? 24 : 30,
                                display:'flex', alignItems:'center', justifyContent:'center',
                                borderRadius:6, border:'1px solid #27b36a', background:'#4cd964', color:'#fff',
                                boxShadow:'0 2px 8px rgba(0,0,0,0.12)', cursor:'pointer', fontSize: responsive ? 12 : 15
                              }}
                            >💾</button>
                            <button
                              onClick={cancelEditing} title="Annuler Modif"
                              style={{
                                width: responsive ? 24 : 30,
                                height: responsive ? 24 : 30,
                                display:'flex', alignItems:'center', justifyContent:'center',
                                borderRadius:6, border:'1px solid #b33', background:'#eee', color:'#b33',
                                boxShadow:'0 2px 8px rgba(0,0,0,0.08)', cursor:'pointer', fontSize: responsive ? 12 : 15
                              }}
                            >✖️</button>
                          </div>
                        }
                      />
                    </div>
                  ) : (
                    <SortableScheduleRow
                      key={s.id}
                      s={s}
                      responsive={responsive}
                      scheduleDispStyle={scheduleDispStyle}
                      formatDaysDisplay={formatDaysDisplay}
                      renderTime={(type, hOrEvent, mOrOffset) => renderSunIconOnly(type, hOrEvent, mOrOffset, { bigger: !responsive })}
                      startEditing={startEditing}
                      deleteSchedule={deleteSchedule}
                    />
                  )
                )}
              </SortableContext>
            </DndContext>
          </div>
        </div>

        {/* ------- NOUVEAU PLANNING -------- */}
        <div style={{ marginTop: responsive ? 10 : 22, maxWidth:'100vw', marginLeft:'auto', marginRight:'auto' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <strong style={{ fontSize: responsive ? 12.7 : 16.5, textDecoration:'underline' }}>Nouveau Planning :</strong>
            <button
              type="button" onClick={openNewDial} title="Régler avec le cadran horaire"
              style={{
                display:'inline-flex', alignItems:'center', gap:5, border:'1px solid #3f4140',
                background:'#f9f9f9', color:'#262927', borderRadius:6, fontWeight:500, cursor:'pointer',
                fontSize: responsive ? 11 : 13, padding: responsive ? '3px 7px' : '4px 10px'
              }}
            >🕐 Cadran</button>
          </div>

          <DaysPicker
            responsive={responsive}
            days={days}
            selectedDaysForNew={selectedDaysForNew}
            setSelectedDaysForNew={setSelectedDaysForNew}
            toggleDaySelection={(i)=>setSelectedDaysForNew(prev => (prev.includes(i)?prev.filter(d=>d!==i):[...prev,i]))}
          />

          <NewScheduleRow
            responsive={responsive}
            HOUR_W={HOUR_W}
            MIN_W={MIN_W}
            GAP_W={GAP_W}
            HM_GAP={HM_GAP}
            DF_GAP={DF_GAP_NEW}
            onHourType={onHourType}
            onHourValue={onHourValue}
            onMinuteOrOffset={onMinuteOrOffset}
            setOnHourType={setOnHourType}
            setOnHourValue={setOnHourValue}
            setOnMinuteOrOffset={setOnMinuteOrOffset}
            offHourType={offHourType}
            offHourValue={offHourValue}
            offMinuteOrOffset={offMinuteOrOffset}
            setOffHourType={setOffHourType}
            setOffHourValue={setOffHourValue}
            setOffMinuteOrOffset={setOffMinuteOrOffset}
            addSchedule={addSchedule}
            showActions={newDirty}
            onCancelNew={resetNewInputs}
          />
        </div>
      </div>

      {/* ------- POPUP CADRAN : nouveau planning ------- */}
      {dialOpenNew && (
        <DialModal
          title={`Nouveau planning — ${device.name || device.device_name}`}
          responsive={responsive}
          onClose={() => setDialOpenNew(false)}
          onSave={addSchedule}
        >
          <DaysPicker
            responsive={responsive}
            days={days}
            selectedDaysForNew={selectedDaysForNew}
            setSelectedDaysForNew={setSelectedDaysForNew}
            toggleDaySelection={(i)=>setSelectedDaysForNew(prev => (prev.includes(i)?prev.filter(d=>d!==i):[...prev,i]))}
          />
          {sunTimes && (
            <div style={{ display:'flex', gap: responsive ? 14 : 20, fontSize: responsive ? 11.5 : 13, color:'#555' }}>
              <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                <GiSunrise style={{ fontSize: responsive ? 13 : 15, color:'#fa4d1d' }} />
                Lever : <strong>{sunTimes.sunrise}</strong>
              </span>
              <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                <GiSunset style={{ fontSize: responsive ? 13 : 15, color:'#e0123b' }} />
                Coucher : <strong>{sunTimes.sunset}</strong>
              </span>
            </div>
          )}
          <div>
            <span style={{ fontSize: responsive ? 10.5 : 12.5, color:'#777', fontWeight:600 }}>Réglage précis :</span>
            <NewScheduleRow
              responsive={responsive}
              HOUR_W={HOUR_W}
              MIN_W={MIN_W}
              GAP_W={GAP_W}
              HM_GAP={HM_GAP}
              DF_GAP={DF_GAP_NEW}
              onHourType={onHourType}
              onHourValue={onHourValue}
              onMinuteOrOffset={onMinuteOrOffset}
              setOnHourType={setOnHourType}
              setOnHourValue={setOnHourValue}
              setOnMinuteOrOffset={setOnMinuteOrOffset}
              offHourType={offHourType}
              offHourValue={offHourValue}
              offMinuteOrOffset={offMinuteOrOffset}
              setOffHourType={setOffHourType}
              setOffHourValue={setOffHourValue}
              setOffMinuteOrOffset={setOffMinuteOrOffset}
              addSchedule={addSchedule}
              showActions={false}
              onCancelNew={resetNewInputs}
            />
          </div>
          <ScheduleDial
            sunrise={sunTimes?.sunrise}
            sunset={sunTimes?.sunset}
            onHourType={onHourType || 'fixed'}
            onHourValue={onHourValue ?? '19'}
            onMinuteOrOffset={onMinuteOrOffset ?? '00'}
            setOnHourType={setOnHourType}
            setOnHourValue={setOnHourValue}
            setOnMinuteOrOffset={setOnMinuteOrOffset}
            offHourType={offHourType || 'fixed'}
            offHourValue={offHourValue ?? '23'}
            offMinuteOrOffset={offMinuteOrOffset ?? '00'}
            setOffHourType={setOffHourType}
            setOffHourValue={setOffHourValue}
            setOffMinuteOrOffset={setOffMinuteOrOffset}
          />
        </DialModal>
      )}

      {/* ------- POPUP CADRAN : édition d'un planning existant ------- */}
      {dialOpenEdit && editingScheduleId != null && (
        <DialModal
          title={`Modifier le planning — ${device.name || device.device_name}`}
          responsive={responsive}
          onClose={() => setDialOpenEdit(false)}
          onSave={saveScheduleEdit}
        >
          <DaysPicker
            responsive={responsive}
            days={days}
            selectedDaysForNew={editingDays}
            setSelectedDaysForNew={setEditingDays}
            toggleDaySelection={(i)=>setEditingDays(prev => (prev.includes(i)?prev.filter(d=>d!==i):[...prev,i]))}
          />
          {sunTimes && (
            <div style={{ display:'flex', gap: responsive ? 14 : 20, fontSize: responsive ? 11.5 : 13, color:'#555' }}>
              <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                <GiSunrise style={{ fontSize: responsive ? 13 : 15, color:'#fa4d1d' }} />
                Lever : <strong>{sunTimes.sunrise}</strong>
              </span>
              <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                <GiSunset style={{ fontSize: responsive ? 13 : 15, color:'#e0123b' }} />
                Coucher : <strong>{sunTimes.sunset}</strong>
              </span>
            </div>
          )}
          <div>
            <span style={{ fontSize: responsive ? 10.5 : 12.5, color:'#777', fontWeight:600 }}>Réglage précis :</span>
            <EditTimesRow
              responsive={responsive}
              HOUR_W={HOUR_W_EDIT}
              MIN_W={MIN_W_EDIT}
              GAP_W={GAP_W}
              HM_GAP={HM_GAP}
              DF_GAP={DF_GAP_EDIT}
              editingOnType={editingOnType}
              editingOnValue={editingOnValue}
              editingOnMinuteOrOffset={editingOnMinuteOrOffset}
              setEditingOnType={setEditingOnType}
              setEditingOnValue={setEditingOnValue}
              setEditingOnMinuteOrOffset={setEditingOnMinuteOrOffset}
              editingOffType={editingOffType}
              editingOffValue={editingOffValue}
              editingOffMinuteOrOffset={editingOffMinuteOrOffset}
              setEditingOffType={setEditingOffType}
              setEditingOffValue={setEditingOffValue}
              setEditingOffMinuteOrOffset={setEditingOffMinuteOrOffset}
            />
          </div>
          <ScheduleDial
            sunrise={sunTimes?.sunrise}
            sunset={sunTimes?.sunset}
            onHourType={editingOnType}
            onHourValue={editingOnValue}
            onMinuteOrOffset={editingOnMinuteOrOffset}
            setOnHourType={setEditingOnType}
            setOnHourValue={setEditingOnValue}
            setOnMinuteOrOffset={setEditingOnMinuteOrOffset}
            offHourType={editingOffType}
            offHourValue={editingOffValue}
            offMinuteOrOffset={editingOffMinuteOrOffset}
            setOffHourType={setEditingOffType}
            setOffHourValue={setEditingOffValue}
            setOffMinuteOrOffset={setEditingOffMinuteOrOffset}
          />
        </DialModal>
      )}
    </div>
  );
}

/* ---------- Popup dédiée : jours + cadran + Enregistrer/Annuler ---------- */
function DialModal({ title, responsive, onClose, onSave, children }) {
  return (
    <div
      onClick={onClose}
      style={{
        position:'fixed', top:0, left:0, width:'100vw', height:'100vh',
        background:'rgba(0,0,0,0.35)', display:'flex', alignItems:'center', justifyContent:'center',
        zIndex:2500, padding:14
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background:'#fff', borderRadius: responsive ? 10 : 14, boxShadow:'0 10px 40px rgba(0,0,0,0.25)',
          width:'100%', maxWidth: responsive ? '96vw' : 640, maxHeight:'92vh',
          display:'flex', flexDirection:'column', overflow:'hidden'
        }}
      >
        <div style={{
          padding: responsive ? '14px 14px 0' : '24px 24px 0', display:'flex', flexDirection:'column',
          gap: responsive ? 12 : 16, overflowY:'auto', overflowX:'hidden', flex:'1 1 auto', minHeight:0
        }}>
          <strong style={{ fontSize: responsive ? 14 : 17 }}>{title}</strong>
          {children}
        </div>
        <div style={{
          display:'flex', gap:10, justifyContent:'flex-end', flex:'0 0 auto',
          padding: responsive ? '12px 14px' : '16px 24px', borderTop:'1px solid #eee'
        }}>
          <button
            type="button" onClick={onClose} title="Annuler"
            style={{
              padding: responsive ? '7px 14px' : '9px 18px', borderRadius:8, border:'1px solid #b33',
              background:'#eee', color:'#b33', cursor:'pointer', fontWeight:500, fontSize: responsive ? 13 : 14.5
            }}
          >Annuler</button>
          <button
            type="button" onClick={onSave} title="Enregistrer"
            style={{
              padding: responsive ? '7px 14px' : '9px 18px', borderRadius:8, border:'1px solid #27b36a',
              background:'#4cd964', color:'#fff', cursor:'pointer', fontWeight:600, fontSize: responsive ? 13 : 14.5
            }}
          >Enregistrer</button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Sous-composants ---------- */
function DaysPicker({ responsive, days, selectedDaysForNew, setSelectedDaysForNew, toggleDaySelection }) {
  const BTN_H = responsive ? 24 : 29;

  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(8, 1fr)', gap: responsive ? 3 : 6, marginTop:7, width:'100%' }}>
      <button
        type="button"
        className={`day-toggle-btn${selectedDaysForNew.length === 7 ? ' day-toggle-selected' : ''}`}
        onClick={() => { if (selectedDaysForNew.length === 7) setSelectedDaysForNew([]); else setSelectedDaysForNew([0,1,2,3,4,5,6]); }}
        style={{
          width:'100%', height:BTN_H, padding: responsive ? '2px 2px' : '4px 4px', fontSize: responsive ? 10.5 : 13,
          borderRadius:4, cursor:'pointer', fontWeight:500,
          whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', textAlign:'center'
        }}
        title="Tous les jours"
      >Tous</button>
      {days.map((d,i) => {
        const selected = selectedDaysForNew.includes(i);
        return (
          <button
            key={i} type="button" onClick={() => toggleDaySelection(i)}
            className={`day-toggle-btn${selected ? ' day-toggle-selected' : ''}`}
            style={{
              width:'100%', height:BTN_H, padding: responsive ? '2px 2px' : '4px 4px', fontSize: responsive ? 10.5 : 13,
              borderRadius:4, cursor:'pointer', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', textAlign:'center', fontWeight:500
            }}
            title={d}
          >{d}</button>
        );
      })}
    </div>
  );
}

/* ===== Row Edition (existants) ===== */
function EditTimesRow({
  responsive, HOUR_W, MIN_W, GAP_W, HM_GAP, DF_GAP,
  editingOnType, editingOnValue, editingOnMinuteOrOffset,
  setEditingOnType, setEditingOnValue, setEditingOnMinuteOrOffset,
  editingOffType, editingOffValue, editingOffMinuteOrOffset,
  setEditingOffType, setEditingOffValue, setEditingOffMinuteOrOffset,
  rightActions
}) {
  const stylesHour = makeSelectStyles(HOUR_W, { valueFS: responsive ? 14 : 15 });
  const stylesMin  = makeSelectStyles(MIN_W,  { valueFS: responsive ? 14 : 15 });

  return (
    <div style={{ display:'flex', alignItems:'flex-end', width:'100%', gap:GAP_W, flexWrap:'nowrap', overflowX:'hidden' }}>
      {/* Début */}
      <div style={{ ...noShrink, display:'flex', flexDirection:'column', minWidth:HOUR_W, maxWidth:260 }}>
        <span style={{ fontWeight:700, marginBottom:2, fontSize: responsive ? 10.2 : 14.2, color:'#197' }}>Début :</span>
        <div style={{ ...noShrink, display:'grid', gridAutoFlow:'column', alignItems:'center', columnGap:HM_GAP }}>
          <div style={{ ...noShrink, width:HOUR_W }}>
            <Select
              options={HOURS_PLACEHOLDER_OPT.concat(SUN_EVENTS, FIXED_HOURS)}
              getOptionLabel={(opt)=>opt.label}
              getOptionValue={(opt)=>opt.value}
              value={[...SUN_EVENTS, ...FIXED_HOURS].find(opt =>
                editingOnType === 'sun'
                  ? opt.type === 'sun' && opt.value === editingOnValue
                  : opt.type === 'fixed' && opt.value === editingOnValue
              )}
              onChange={(val)=>{
                if (val.type==='sun'){ setEditingOnType('sun'); setEditingOnValue(val.value); setEditingOnMinuteOrOffset(0); }
                else if (val.type==='fixed'){ setEditingOnType('fixed'); setEditingOnValue(val.value); setEditingOnMinuteOrOffset('00'); }
              }}
              styles={stylesHour}
              components={SELECT_SUN_ICON}
              isSearchable={false}
              menuPlacement="auto"
              menuPortalTarget={selectMenuPortalTarget}
              placeholder={HOURS_PLACEHOLDER_TEXT}
            />
          </div>
          <div style={{ ...noShrink, width:MIN_W }}>
            {editingOnType === 'sun' ? (
              <Select
                options={MINUTES_PLACEHOLDER_OPT.concat(SUN_OFFSETS)}
                getOptionLabel={(opt)=>opt.label}
                getOptionValue={(opt)=>String(opt.value)}
                value={SUN_OFFSETS.find(o => o.value === Number(editingOnMinuteOrOffset))}
                onChange={(opt)=>setEditingOnMinuteOrOffset(opt.value)}
                styles={stylesMin}
                components={SELECT_NO_ARROW}
                isSearchable={false}
                menuPlacement="auto"
                menuPortalTarget={selectMenuPortalTarget}
                placeholder={MINUTES_PLACEHOLDER_TEXT}
              />
            ) : (
              <Select
                options={MINUTES_PLACEHOLDER_OPT.concat(FIXED_MINUTES)}
                getOptionLabel={(opt)=>opt.label}
                getOptionValue={(opt)=>opt.value}
                value={FIXED_MINUTES.find(o => o.value === (editingOnMinuteOrOffset || '00'))}
                onChange={(opt)=>setEditingOnMinuteOrOffset(opt.value)}
                styles={stylesMin}
                components={SELECT_NO_ARROW}
                isSearchable={false}
                menuPlacement="auto"
                menuPortalTarget={selectMenuPortalTarget}
                placeholder={MINUTES_PLACEHOLDER_TEXT}
              />
            )}
          </div>
        </div>
      </div>

      {/* Spacer Début ↔ Fin (existant) */}
      <div style={{ ...noShrink, width:DF_GAP, minWidth:DF_GAP, height:1 }} />

      {/* Fin */}
      <div style={{ ...noShrink, display:'flex', flexDirection:'column', minWidth:HOUR_W, maxWidth:260 }}>
        <span style={{ fontWeight:700, marginBottom:2, fontSize: responsive ? 10.2 : 14.2, color:'#b61' }}>Fin :</span>
        <div style={{ ...noShrink, display:'grid', gridAutoFlow:'column', alignItems:'center', columnGap:HM_GAP }}>
          <div style={{ ...noShrink, width:HOUR_W }}>
            <Select
              options={HOURS_PLACEHOLDER_OPT.concat(SUN_EVENTS, FIXED_HOURS)}
              getOptionLabel={(opt)=>opt.label}
              getOptionValue={(opt)=>opt.value}
              value={[...SUN_EVENTS, ...FIXED_HOURS].find(opt =>
                editingOffType === 'sun'
                  ? opt.type === 'sun' && opt.value === editingOffValue
                  : opt.type === 'fixed' && opt.value === editingOffValue
              )}
              onChange={(val)=>{
                if (val.type==='sun'){ setEditingOffType('sun'); setEditingOffValue(val.value); setEditingOffMinuteOrOffset(0); }
                else if (val.type==='fixed'){ setEditingOffType('fixed'); setEditingOffValue(val.value); setEditingOffMinuteOrOffset('00'); }
              }}
              styles={stylesHour}
              components={SELECT_SUN_ICON}
              isSearchable={false}
              menuPlacement="auto"
              menuPortalTarget={selectMenuPortalTarget}
              placeholder={HOURS_PLACEHOLDER_TEXT}
            />
          </div>
          <div style={{ ...noShrink, width:MIN_W }}>
            {editingOffType === 'sun' ? (
              <Select
                options={MINUTES_PLACEHOLDER_OPT.concat(SUN_OFFSETS)}
                getOptionLabel={(opt)=>opt.label}
                getOptionValue={(opt)=>String(opt.value)}
                value={SUN_OFFSETS.find(o => o.value === Number(editingOffMinuteOrOffset))}
                onChange={(opt)=>setEditingOffMinuteOrOffset(opt.value)}
                styles={stylesMin}
                components={SELECT_NO_ARROW}
                isSearchable={false}
                menuPlacement="auto"
                menuPortalTarget={selectMenuPortalTarget}
                placeholder={MINUTES_PLACEHOLDER_TEXT}
              />
            ) : (
              <Select
                options={MINUTES_PLACEHOLDER_OPT.concat(FIXED_MINUTES)}
                getOptionLabel={(opt)=>opt.label}
                getOptionValue={(opt)=>opt.value}
                value={FIXED_MINUTES.find(o => o.value === (editingOffMinuteOrOffset || '00'))}
                onChange={(opt)=>setEditingOffMinuteOrOffset(opt.value)}
                styles={stylesMin}
                components={SELECT_NO_ARROW}
                isSearchable={false}
                menuPlacement="auto"
                menuPortalTarget={selectMenuPortalTarget}
                placeholder={MINUTES_PLACEHOLDER_TEXT}
              />
            )}
          </div>
        </div>
      </div>

      {/* Actions à droite */}
      {rightActions && (
        <div style={{ ...noShrink, marginLeft:'auto', display:'flex', alignItems:'flex-end' }}>
          {rightActions}
        </div>
      )}
    </div>
  );
}

/* ===== Row Nouveau planning ===== */
function NewScheduleRow({
  responsive, HOUR_W, MIN_W, GAP_W, HM_GAP, DF_GAP,
  onHourType, onHourValue, onMinuteOrOffset, setOnHourType, setOnHourValue, setOnMinuteOrOffset,
  offHourType, offHourValue, offMinuteOrOffset, setOffHourType, setOffHourValue, setOffMinuteOrOffset,
  addSchedule, showActions=false, onCancelNew
}) {
  const stylesHour = makeSelectStyles(HOUR_W, { placeholderFS: responsive ? 12 : 15 });
  const stylesMin  = makeSelectStyles(MIN_W,   { placeholderFS: responsive ? 12 : 15 });

  return (
    <div style={{ marginTop:9, display:'flex', flexDirection:'row', gap:GAP_W, alignItems:'flex-end', flexWrap:'nowrap', minWidth: responsive ? 30 : 520, overflowX:'auto' }}>
      {/* Début */}
      <div style={{ ...noShrink, display:'flex', flexDirection:'column', minWidth:HOUR_W, maxWidth:260 }}>
        <span style={{ fontWeight:700, marginBottom:2, fontSize: responsive ? 10.2 : 14.2, color:'#197' }}>Début :</span>
        <div style={{ ...noShrink, display:'grid', gridAutoFlow:'column', alignItems:'center', columnGap:HM_GAP }}>
          <div style={{ ...noShrink, width:HOUR_W }}>
            <Select
              options={HOURS_PLACEHOLDER_OPT.concat(SUN_EVENTS, FIXED_HOURS)}
              getOptionLabel={(opt)=>opt.label}
              getOptionValue={(opt)=>opt.value}
              value={onHourValue ? [...SUN_EVENTS, ...FIXED_HOURS].find(o =>
                (onHourType==='sun'   && o.type==='sun'   && o.value===onHourValue) ||
                (onHourType==='fixed' && o.type==='fixed' && o.value===onHourValue)
              ) : null}
              onChange={(val)=>{
                if (val?.type==='sun'){ setOnHourType('sun'); setOnHourValue(val.value); setOnMinuteOrOffset(0); }
                else if (val?.type==='fixed'){ setOnHourType('fixed'); setOnHourValue(val.value); setOnMinuteOrOffset('00'); }
              }}
              styles={stylesHour}
              components={SELECT_SUN_ICON}
              isSearchable={false}
              menuPlacement="auto"
              menuPortalTarget={selectMenuPortalTarget}
              placeholder={HOURS_PLACEHOLDER_TEXT}
            />
          </div>
          <div style={{ ...noShrink, width:MIN_W }}>
            {onHourType === null ? (
              <Select
                options={MINUTES_PLACEHOLDER_OPT.concat(FIXED_MINUTES)}
                styles={stylesMin}
                components={SELECT_NO_ARROW}
                isSearchable={false}
                isDisabled
                placeholder={MINUTES_PLACEHOLDER_TEXT}
                menuPortalTarget={selectMenuPortalTarget}
              />
            ) : onHourType === 'sun' ? (
              <Select
                options={MINUTES_PLACEHOLDER_OPT.concat(SUN_OFFSETS)}
                getOptionLabel={(opt)=>opt.label}
                getOptionValue={(opt)=>String(opt.value)}
                value={onMinuteOrOffset != null ? SUN_OFFSETS.find(o => o.value === onMinuteOrOffset) : null}
                onChange={(opt)=>setOnMinuteOrOffset(opt.value)}
                styles={stylesMin}
                components={SELECT_NO_ARROW}
                isSearchable={false}
                menuPlacement="auto"
                menuPortalTarget={selectMenuPortalTarget}
                placeholder={MINUTES_PLACEHOLDER_TEXT}
              />
            ) : (
              <Select
                options={MINUTES_PLACEHOLDER_OPT.concat(FIXED_MINUTES)}
                getOptionLabel={(opt)=>opt.label}
                getOptionValue={(opt)=>opt.value}
                value={onMinuteOrOffset != null ? FIXED_MINUTES.find(o => o.value === onMinuteOrOffset) : null}
                onChange={(opt)=>setOnMinuteOrOffset(opt.value)}
                styles={stylesMin}
                components={SELECT_NO_ARROW}
                isSearchable={false}
                menuPlacement="auto"
                menuPortalTarget={selectMenuPortalTarget}
                placeholder={MINUTES_PLACEHOLDER_TEXT}
              />
            )}
          </div>
        </div>
      </div>

      {/* Spacer Début ↔ Fin (nouveau) */}
      <div style={{ ...noShrink, width:DF_GAP, minWidth:DF_GAP, height:1 }} />

      {/* Fin */}
      <div style={{ ...noShrink, display:'flex', flexDirection:'column', minWidth:HOUR_W, maxWidth:260 }}>
        <span style={{ fontWeight:700, marginBottom:2, fontSize: responsive ? 10.2 : 14.2, color:'#b61' }}>Fin :</span>
        <div style={{ ...noShrink, display:'grid', gridAutoFlow:'column', alignItems:'center', columnGap:HM_GAP }}>
          <div style={{ ...noShrink, width:HOUR_W }}>
            <Select
              options={HOURS_PLACEHOLDER_OPT.concat(SUN_EVENTS, FIXED_HOURS)}
              getOptionLabel={(opt)=>opt.label}
              getOptionValue={(opt)=>opt.value}
              value={offHourValue ? [...SUN_EVENTS, ...FIXED_HOURS].find(o =>
                (offHourType==='sun'   && o.type==='sun'   && o.value===offHourValue) ||
                (offHourType==='fixed' && o.type==='fixed' && o.value===offHourValue)
              ) : null}
              onChange={(val)=>{
                if (val?.type==='sun'){ setOffHourType('sun'); setOffHourValue(val.value); setOffMinuteOrOffset(0); }
                else if (val?.type==='fixed'){ setOffHourType('fixed'); setOffHourValue(val.value); setOffMinuteOrOffset('00'); }
              }}
              styles={stylesHour}
              components={SELECT_SUN_ICON}
              isSearchable={false}
              menuPlacement="auto"
              menuPortalTarget={selectMenuPortalTarget}
              placeholder={HOURS_PLACEHOLDER_TEXT}
            />
          </div>
          <div style={{ ...noShrink, width:MIN_W }}>
            {offHourType === null ? (
              <Select
                options={MINUTES_PLACEHOLDER_OPT.concat(FIXED_MINUTES)}
                styles={stylesMin}
                components={SELECT_NO_ARROW}
                isSearchable={false}
                isDisabled
                placeholder={MINUTES_PLACEHOLDER_TEXT}
                menuPortalTarget={selectMenuPortalTarget}
              />
            ) : offHourType === 'sun' ? (
              <Select
                options={MINUTES_PLACEHOLDER_OPT.concat(SUN_OFFSETS)}
                getOptionLabel={(opt)=>opt.label}
                getOptionValue={(opt)=>String(opt.value)}
                value={offMinuteOrOffset != null ? SUN_OFFSETS.find(o => o.value === offMinuteOrOffset) : null}
                onChange={(opt)=>setOffMinuteOrOffset(opt.value)}
                styles={stylesMin}
                components={SELECT_NO_ARROW}
                isSearchable={false}
                menuPlacement="auto"
                menuPortalTarget={selectMenuPortalTarget}
                placeholder={MINUTES_PLACEHOLDER_TEXT}
              />
            ) : (
              <Select
                options={MINUTES_PLACEHOLDER_OPT.concat(FIXED_MINUTES)}
                getOptionLabel={(opt)=>opt.label}
                getOptionValue={(opt)=>opt.value}
                value={offMinuteOrOffset != null ? FIXED_MINUTES.find(o => o.value === offMinuteOrOffset) : null}
                onChange={(opt)=>setOffMinuteOrOffset(opt.value)}
                styles={stylesMin}
                components={SELECT_NO_ARROW}
                isSearchable={false}
                menuPlacement="auto"
                menuPortalTarget={selectMenuPortalTarget}
                placeholder={MINUTES_PLACEHOLDER_TEXT}
              />
            )}
          </div>
        </div>
      </div>

      {/* Actions à droite */}
      {showActions && (
        <div style={{ ...noShrink, marginLeft:'auto', alignSelf:'flex-end', display:'flex', gap: responsive ? 5 : 10 }}>
          <button
            onClick={addSchedule} title="Valider Nouveau"
            style={{ width: responsive ? 30 : 44, height: responsive ? 30 : 44, display:'flex', alignItems:'flex-end', justifyContent:'center',
              borderRadius:8, border:'1px solid #27b36a', background:'#4cd964', color:'#fff', boxShadow:'0 2px 8px rgba(0,0,0,0.12)', cursor:'pointer', fontSize: responsive ? 15 : 20 }}
          >💾</button>
          <button
            onClick={onCancelNew} title="Annuler Nouveau"
            style={{ width: responsive ? 30 : 44, height: responsive ? 30 : 44, display:'flex', alignItems:'flex-end', justifyContent:'center',
              borderRadius:8, border:'1px solid #b33', background:'#eee', color:'#b33', boxShadow:'0 2px 8px rgba(0,0,0,0.08)', cursor:'pointer', fontSize: responsive ? 15 : 20 }}
          >✖️</button>
        </div>
      )}
    </div>
  );
}

export default DeviceCard;